import { styled, Text } from '@obvio/app'
import { SvgEdit } from '@obvio/svg'
import {
  Button,
  CheckboxInput,
  Stack,
  TextInput,
  useFormContext,
} from '@obvio/ui'
import { useState } from 'react'

import { FormArtworksList } from './FormArtworskList'
import { FormContinue } from './FormContinue'
import { FormDetails } from './FormDetails'
import { FormJoinPersonal } from './FormJoinPersonal'

import type { ReactElement } from 'react'

const Block = styled.div`
  padding: ${(theme) => theme.spacing.medium};
  position: relative;
  border: 1px solid ${(theme) => theme.colors.additional[2]};
  width: 100%;
  text-align: left;
`

const SaveButton = styled(Button)`
  margin-top: ${(theme) => theme.spacing.medium};
  margin-left: auto !important;
  margin-right: 0;
  display: block;
`

const Title = styled(Text)`
  display: block;
  margin-bottom: ${(theme) => theme.spacing.extraSmall};
`

const EditButton = styled(Button)`
  position: absolute;
  top: ${(theme) => theme.spacing.medium};
  right: ${(theme) => theme.spacing.medium};
  height: auto !important;
`

type CardProps = {
  title: string
  children: ({ editing }: { editing: boolean }) => ReactElement | ReactElement[]
}

function Card({ children, title }: CardProps): ReactElement {
  const [editing, setEditing] = useState(false)
  return (
    <Block>
      <Title tag="span">{title}</Title>
      {children({ editing })}
      {editing ? (
        <SaveButton onClick={() => setEditing(false)}>Save</SaveButton>
      ) : (
        <EditButton
          kind={['icon', 'transparent']}
          onClick={() => setEditing(true)}
        >
          <SvgEdit height="1.25rem" />
        </EditButton>
      )}
    </Block>
  )
}

function ContactCard(): ReactElement {
  const { getValue } = useFormContext()
  return (
    <Card title="CONTACT">
      {({ editing }) =>
        editing ? (
          <>
            <TextInput name="email" label="Email" />
            <TextInput name="phone" label="Phone" />
          </>
        ) : (
          <>
            <Text>{getValue('email')}</Text>
            <Text>{getValue('phone')}</Text>
          </>
        )
      }
    </Card>
  )
}

function InfoCard(): ReactElement {
  const { getValue } = useFormContext()
  return (
    <Card title="SUPPORTING INFORMATION">
      {({ editing }) =>
        editing ? (
          <FormJoinPersonal />
        ) : (
          <>
            <Text>Full name: {getValue('name')}</Text>
            <Text>site: {getValue('site')}</Text>
            <Text>social media:{getValue('social')}</Text>
          </>
        )
      }
    </Card>
  )
}

function DetailsCard(): ReactElement {
  const { getValue, getInputs } = useFormContext()

  const inputs = getInputs()
  const getListValue = (name: string) =>
    inputs?.[name]?.map(([, v]: [string, string]) => v).join('\n') || '-'
  return (
    <Card title="DETAILS">
      {({ editing }) =>
        editing ? (
          <FormDetails />
        ) : (
          <>
            <Text>About you:</Text>
            <Text>{getValue('biogram')}</Text>
            <br />
            <Text>Statement:</Text>
            <Text>{getValue('statement')}</Text>
            <br />
            <Text>Exhibitions:</Text>
            <Text>{getListValue('exhibitions')}</Text>
            <br />
            <Text>Awards and residencies:</Text>
            <Text>{getListValue('awards')}</Text>
            <br />
            <Text>Publications:</Text>
            <Text>{getListValue('publications')}</Text>
          </>
        )
      }
    </Card>
  )
}

export function FormJoinSummary(): ReactElement {
  return (
    <Stack kind="vertical">
      <ContactCard />
      <InfoCard />
      <DetailsCard />
      <FormArtworksList />
      <CheckboxInput
        name="terms"
        noSubmit
        required
        label="I agree to the GDPR and Privacy Policy and consent to the processing of my personal data for the purposes of this application."
      />
      <CheckboxInput
        name="rodo"
        noSubmit
        required
        label="I consent to being contacted by mail or telephone for the purpose of this application."
      />
      <CheckboxInput
        name="ownership"
        noSubmit
        required
        label="I declare that I am the owner of the copyright of all media and content uploaded into this form and grant permission to the gallery to use it for the purpose of this application."
      />
      <FormContinue />
    </Stack>
  )
}
